/**
 * 统一数据字典加载Hook
 * 提供页面加载时直接从storage获取数据字典的公共方法
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { unifiedDictManager } from '../utils/unifiedDictManager';

// 导出DictItem类型供其他模块使用
export interface DictItem {
  id: number;
  dictLabel: string;
  dictValue: string;
  dictSort: number;
  status: number;
  cssClass?: string;
  listClass?: string;
  isDefault?: string;
  remark?: string;
}

export interface DictOptions {
  dictType: string;
  labelKey?: string; // 显示字段，默认 'dictLabel'
  valueKey?: string; // 值字段，默认 'dictValue'
  sortKey?: string; // 排序字段，默认 'dictSort'
  filter?: (item: DictItem) => boolean; // 过滤函数
  transform?: (item: DictItem) => any; // 转换函数
}

// 全局字典数据缓存，避免重复加载
const globalDictCache = new Map<string, DictItem[]>();
const globalLoadingPromises = new Map<string, Promise<DictItem[]>>();

/**
 * 清理全局字典缓存
 */
export const clearGlobalDictCache = () => {
  globalDictCache.clear();
  globalLoadingPromises.clear();
};

/**
 * 统一数据字典Hook
 * 优先从storage获取，按需从API获取
 */
export const useDict = (dictType: string, options: DictOptions = { dictType }) => {
  const [dictData, setDictData] = useState<DictItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const {
    labelKey = 'dictLabel',
    valueKey = 'dictValue',
    sortKey = 'dictSort',
    filter,
    transform
  } = options;

  // 加载字典数据
  const loadDictData = useCallback(async () => {
    if (!dictType) return;
    
    // 展示网站不需要token也能加载字典数据，允许无token访问
    //   return;
    // }

    // 检查全局缓存
    if (globalDictCache.has(dictType)) {
      const cachedData = globalDictCache.get(dictType)!;
      if (isMountedRef.current) {
        setDictData(cachedData);
      }
      return;
    }

    // 检查是否正在加载中
    const existingPromise = globalLoadingPromises.get(dictType);
    if (existingPromise) {
      try {
        const data = await existingPromise;
        if (isMountedRef.current) {
          setDictData(data);
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError(err instanceof Error ? err.message : '加载字典数据失败');
        }
      }
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // 确保统一字典管理器已初始化（只初始化一次，从localStorage加载缓存）
      if (!unifiedDictManager.isReady()) {
        await unifiedDictManager.initialize();
      }
      
      // 从统一字典管理器获取数据（优先从缓存读取，不会触发API调用）
      // 字典数据已经在登录时全部加载到本地，这里只从缓存读取
      const promise = unifiedDictManager.getDictData(dictType);
      globalLoadingPromises.set(dictType, promise);
      
      const data = await promise;
      
      // 更新全局缓存
      globalDictCache.set(dictType, data);
      
      // 应用过滤条件
      let processedData = data;
      if (filter) {
        processedData = processedData.filter(filter);
      }
      
      // 应用转换函数
      if (transform) {
        processedData = processedData.map(transform);
      }
      
      // 按排序字段排序
      processedData.sort((a: any, b: any) => (a[sortKey] || 0) - (b[sortKey] || 0));
      
      if (isMountedRef.current) {
        setDictData(processedData);
      }
    } catch (err) {
      console.error(`加载字典数据失败 (${dictType}):`, err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : '加载字典数据失败，请稍后重试');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
      globalLoadingPromises.delete(dictType);
    }
  }, [dictType, filter, transform, sortKey]);

  // 获取字典选项（用于Select组件）
  const getOptions = useCallback(() => {
    return dictData.map(item => ({
      label: (item as any)[labelKey],
      value: (item as any)[valueKey],
    }));
  }, [dictData, labelKey, valueKey]);

  // 根据值获取标签
  const getLabelByValue = useCallback((value: string | number) => {
    const item = dictData.find(item => (item as any)[valueKey] === value);
    return item ? (item as any)[labelKey] : value;
  }, [dictData, valueKey, labelKey]);

  // 根据标签获取值
  const getValueByLabel = useCallback((label: string) => {
    const item = dictData.find(item => (item as any)[labelKey] === label);
    return item ? (item as any)[valueKey] : label;
  }, [dictData, labelKey, valueKey]);

  // 获取默认值
  const getDefaultValue = useCallback(() => {
    const defaultItem = dictData.find(item => item.isDefault === 'Y');
    return defaultItem ? (defaultItem as any)[valueKey] : null;
  }, [dictData, valueKey]);

  // 刷新数据（仅在必要时使用，比如字典数据更新后）
  // 注意：正常情况下不应该调用refresh，因为字典数据已经在登录时全部加载
  const refresh = useCallback(() => {
    // 清除缓存，强制重新加载（仅在字典数据更新后需要）
    globalDictCache.delete(dictType);
    unifiedDictManager.clearDictCache(dictType);
    loadDictData();
  }, [dictType, loadDictData]);

  useEffect(() => {
    loadDictData();
    
    // 清理函数
    return () => {
      isMountedRef.current = false;
    };
  }, [loadDictData]);

  return {
    dictData,
    options: getOptions(),
    loading,
    error,
    getLabelByValue,
    getValueByLabel,
    getDefaultValue,
    refresh
  };
};

/**
 * 批量获取多个字典类型的数据
 * @param dictTypes 字典类型数组
 * @returns 字典数据映射
 */
export const useDicts = (dictTypes: string[]) => {
  const [dictsData, setDictsData] = useState<Record<string, DictItem[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDictsData = useCallback(async () => {
    if (!dictTypes.length) return;
    
    // 检查用户是否已登录
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setError('用户未登录，无法加载字典数据');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // 确保统一字典管理器已初始化
      if (!unifiedDictManager.isReady()) {
        await unifiedDictManager.initialize();
      }
      
      const promises = dictTypes.map(async (dictType) => {
        const data = await unifiedDictManager.getDictData(dictType);
        return { dictType, data };
      });
      
      const results = await Promise.all(promises);
      const dictsDataMap: Record<string, DictItem[]> = {};
      
      results.forEach(({ dictType, data }) => {
        dictsDataMap[dictType] = data;
      });
      
      setDictsData(dictsDataMap);
    } catch (err) {
      console.error('批量加载字典数据失败:', err);
      setError(err instanceof Error ? err.message : '批量加载字典数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [dictTypes]);

  useEffect(() => {
    loadDictsData();
  }, [loadDictsData]);

  return {
    dictsData,
    loading,
    error,
    refresh: loadDictsData
  };
};

/**
 * 页面级字典数据预加载Hook
 * 在页面加载时预加载所需的字典数据
 */
export const usePageDictPreload = (dictTypes: string[]) => {
  const [isPreloaded, setIsPreloaded] = useState(false);
  const [preloadError, setPreloadError] = useState<string | null>(null);

  const preloadDicts = useCallback(async () => {
    if (!dictTypes.length || isPreloaded) return;
    
    try {
      // 确保统一字典管理器已初始化
      if (!unifiedDictManager.isReady()) {
        await unifiedDictManager.initialize();
      }
      
      // 并行加载所有需要的字典数据
      const promises = dictTypes.map(dictType => 
        unifiedDictManager.getDictData(dictType)
      );
      
      await Promise.all(promises);
      
      // 更新全局缓存
      for (const dictType of dictTypes) {
        const data = await unifiedDictManager.getDictData(dictType);
        globalDictCache.set(dictType, data);
      }
      
      setIsPreloaded(true);
    } catch (error) {
      console.error('页面字典数据预加载失败:', error);
      setPreloadError(error instanceof Error ? error.message : '预加载失败');
    }
  }, [dictTypes, isPreloaded]);

  useEffect(() => {
    preloadDicts();
  }, [preloadDicts]);

  return {
    isPreloaded,
    preloadError,
    preloadDicts
  };
};
